import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Stack, Typography } from "@mui/material";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import {
  ActivityImage,
  DragHandle,
  ExerciseControls,
} from "routes/patients/views/Treatment/components/TreatmentEditor/shared";
import type {
  HandleLevelChange,
  HandleSetsAndRepsChange,
  TimelineActivity,
} from "routes/patients/views/Treatment/components/TreatmentEditor/types";
import { BaseButton } from "shared/atoms/BaseButton";
import { Tooltip } from "shared/atoms/Tooltip";

export type onPinExercise = ({
  activityId,
  exercise_id,
  weekNumber,
}: {
  activityId?: string;
  exercise_id?: number;
  weekNumber?: number;
}) => void;

interface ActivityCardProps {
  activity: TimelineActivity;
  currentWeek?: number;
  exercisesPerDay?: number;
  isDragging?: boolean;
  isMultiSelectMode?: boolean;
  isSelected?: boolean;
  totalPinnedCount?: number;
  weekNumber: number;
  onClick?: (activity: TimelineActivity) => void;
  onCopy?: (activity: TimelineActivity) => void;
  onLevelChange?: HandleLevelChange;
  onPinExercise?: onPinExercise;
  onPreview?: (id: number, part: number) => void;
  onRemove?: (activity: TimelineActivity, weekNumber: number) => void;
  onToggleLevelLock?: HandleLevelChange;
  onSetsAndRepsChange?: HandleSetsAndRepsChange;
}

export const ActivityCard: React.FC<ActivityCardProps> = ({
  activity,
  currentWeek = 1,
  exercisesPerDay = 3,
  isDragging,
  isMultiSelectMode = false,
  isSelected = false,
  totalPinnedCount = 0,
  weekNumber,
  onClick,
  onCopy,
  onLevelChange,
  onPinExercise,
  onPreview,
  onRemove,
  onToggleLevelLock,
  onSetsAndRepsChange,
}) => {
  const { t } = useTranslation();
  const { attributes, listeners, transform, transition, setNodeRef } = useSortable({
    id: activity.id,
    data: {
      type: "activity_card",
      activity,
      activityType: activity.type,
      weekNumber,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isPastWeek = weekNumber < currentWeek;
  const isDragDisabled = activity.level_locked || isPastWeek || !["exercise", "lesson"].includes(activity.type);
  const dragHandleProps = isDragDisabled ? {} : { ...attributes, ...listeners };
  const canPinExercise = activity.pinned || totalPinnedCount < exercisesPerDay;
  const showPinButton = onPinExercise && activity.type === "exercise";

  return (
    <Container
      ref={isDragDisabled ? null : setNodeRef}
      className={clsx({
        dragging: isDragging,
        locked: activity.level_locked,
        "multi-select-mode": isMultiSelectMode,
        selected: isSelected,
      })}
      style={style}
      onClick={() => onClick?.(activity)}
      {...dragHandleProps}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        {!isDragDisabled && <DragHandle isDisabled={activity.level_locked || isPastWeek} />}

        <ActivityImage src={activity.image_url || activity.video_thumbnail_file_url} title={activity.title} />

        <Typography variant="body_bold">
          {activity.title}
          {activity.variation_title ? ` (${activity.variation_title})` : ""}
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          sx={{ flexShrink: 1, justifyContent: "flex-end", flexWrap: "wrap", marginLeft: "auto" }}
        >
          {onPreview && (
            <BaseButton
              variant="action"
              icon="eye"
              size="small"
              title={t("treatment.editor.preview_activity", {
                activityType: activity.type === "exercise" ? "exercise" : "lesson",
              })}
              onClick={event => {
                event.stopPropagation();
                const id = activity.type === "exercise" ? activity.exercise_id : parseInt(activity.id, 10);
                onPreview(id, activity.recommended_level || 1);
              }}
            />
          )}

          {showPinButton && (
            <Tooltip
              arrow
              disabled={canPinExercise}
              placement="top"
              title={t("treatment.editor.pin_exercise.tooltip", { exercisesPerDay })}
            >
              <BaseButton
                className={clsx({ active: "pinned" in activity && activity.pinned })}
                disabled={isPastWeek || !canPinExercise}
                variant="action"
                icon={"pinned" in activity && activity.pinned ? "thumbtack-solid" : "thumbtack"}
                size="small"
                title={t("treatment.editor.pin_exercise.title")}
                onClick={event => {
                  event.stopPropagation();
                  onPinExercise?.({ activityId: activity.id, weekNumber });
                }}
                style={{ flexBasis: "32px" }}
              />
            </Tooltip>
          )}

          {onCopy && (
            <BaseButton
              disabled={isPastWeek}
              variant="action"
              icon="copy"
              size="small"
              title={t("treatment.editor.copy_exercise.tooltip")}
              onClick={event => {
                event.stopPropagation();
                onCopy(activity);
              }}
              style={{ flexBasis: "32px" }}
            />
          )}

          {onRemove && (
            <BaseButton
              disabled={isPastWeek}
              variant="action"
              className="danger"
              icon="trash"
              size="small"
              title={t("treatment.editor.remove_activity", { activityType: activity.type })}
              onClick={event => {
                event.stopPropagation();
                onRemove(activity, weekNumber);
              }}
              style={{ flexBasis: "32px" }}
            />
          )}
        </Stack>
      </Stack>

      {activity.type === "exercise" && (
        <ExerciseControls
          exercise={activity}
          isDisabled={isPastWeek}
          onLevelChange={onLevelChange}
          onSetsAndRepsChange={onSetsAndRepsChange}
          onToggleLevelLock={onToggleLevelLock}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.redesign.db30};
  border-radius: 12px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.redesign.db1};
    border-color: ${({ theme }) => theme.colors.redesign.db40};
  }

  &.dragging {
    opacity: 0.8;
  }

  &.locked {
    border-color: ${({ theme }) => theme.colors.redesign.o60};
    cursor: not-allowed;

    &:hover {
      border-color: ${({ theme }) => theme.colors.redesign.o100};
    }
  }

  &.multi-select-mode {
    cursor: pointer;
  }

  &.selected {
    background-color: ${({ theme }) => theme.colors.redesign.b10};
    border: 2px solid ${({ theme }) => theme.colors.redesign.b100};
  }
`;
